const API_BASE_URL = '/api/adminSettings';

export interface AdminSetting {
  id: number;
  setting_key: string;
  setting_value: string;
  setting_type: 'string' | 'boolean' | 'number' | 'json';
  description?: string;
  category?: string;
  created_at: string;
  updated_at: string;
  updated_by?: string;
}

// Get all admin settings
export const getAllAdminSettings = async (): Promise<AdminSetting[]> => {
  const response = await fetch(`${API_BASE_URL}/settings`, {
    credentials: 'include', // Include cookies/session
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch admin settings');
  }
  return response.json();
};

// Get a specific admin setting by key
export const getAdminSetting = async (key: string): Promise<AdminSetting> => {
  const response = await fetch(`${API_BASE_URL}/settings/${key}`, {
    credentials: 'include', // Add credentials
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch admin setting: ${key}`);
  }
  return response.json();
};

// Get settings by category
export const getAdminSettingsByCategory = async (category: string): Promise<AdminSetting[]> => {
  const response = await fetch(`${API_BASE_URL}/settings/category/${category}`, {
    credentials: 'include', // Add credentials
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch admin settings for category: ${category}`);
  }
  return response.json();
};

// Create a new admin setting
export const createAdminSetting = async (setting: Omit<AdminSetting, 'id' | 'created_at' | 'updated_at'>): Promise<AdminSetting> => {
  const response = await fetch(`${API_BASE_URL}/settings`, {
    method: 'POST',
    credentials: 'include', // Add credentials
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(setting),
  });
  
  if (!response.ok) {
    throw new Error('Failed to create admin setting');
  }
  return response.json();
};

// Update an admin setting
export const updateAdminSetting = async (key: string, value: string): Promise<AdminSetting> => {
  const response = await fetch(`${API_BASE_URL}/settings/${key}`, {
    method: 'PUT',
    credentials: 'include', // Add credentials
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ setting_value: value }),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to update admin setting: ${key}`);
  }
  return response.json();
};

// Delete an admin setting
export const deleteAdminSetting = async (key: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/settings/${key}`, {
    method: 'DELETE',
    credentials: 'include', // Add credentials
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to delete admin setting: ${key}`);
  }
};

// Reset an admin setting to its default value
export const resetAdminSetting = async (key: string): Promise<AdminSetting> => {
  const response = await fetch(`${API_BASE_URL}/settings/${key}/reset`, {
    method: 'POST',
    credentials: 'include', // Add credentials
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to reset admin setting: ${key}`);
  }
  return response.json();
};